import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';

const MissingRate = () => {
  const [step, setStep] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { fetchDatasets(); }, []);

  const fetchDatasets = async () => {
    try { const res = await axios.get('/api/missing-rate/datasets'); setDatasets(res.data.datasets || []); }
    catch { toast.error('Failed to load datasets'); }
  };

  const handleSelectDataset = async () => {
    if (!selectedDatasetId) return;
    setLoading(true);
    try {
      const res = await axios.post('/api/missing-rate/select-dataset', { dataset_id: parseInt(selectedDatasetId) });
      setSelectedDataset(res.data); setResults(null); setStep(1);
      toast.success(`Loaded: ${res.data.rows?.toLocaleString()} rows`);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try { const res = await axios.post('/api/missing-rate/analyze'); setResults(res.data.results); setStep(2); toast.success(res.data.message); }
    catch { toast.error('Analysis failed'); }
    finally { setAnalyzing(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/api/missing-rate/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'missing_rate.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const stepTabs = ['Select', 'Review', 'Results'];
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', fontWeight: 600, fontSize: '14px', color: '#2c3e50' };
  const gcs = { padding: '16px', background: 'white' };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '22px', marginBottom: '16px', color: '#2c3e50' }}>Missing Rate Analysis</h1>

      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}>
        {stepTabs.map((tab, idx) => (
          <button key={idx} onClick={() => { if (idx === 0) setStep(0); else if (idx === 1 && selectedDataset) setStep(1); else if (idx === 2 && results) setStep(2); }}
            style={{ padding: '6px 12px', fontSize: '11px', background: step === idx ? '#1abc9c' : 'transparent', color: step === idx ? 'white' : '#7f8c8d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{tab}</button>
        ))}
      </div>

      {step === 0 && (
        <div style={gs}><div style={ghs}>📊 Select Dataset</div><div style={gcs}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select value={selectedDatasetId} onChange={(e) => setSelectedDatasetId(e.target.value)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
              <option value="">-- Select a dataset --</option>
              {datasets.map((ds) => <option key={ds.id} value={ds.id}>{ds.name} ({ds.rows?.toLocaleString()} rows)</option>)}
            </select>
            <button onClick={handleSelectDataset} disabled={!selectedDatasetId || loading} style={{ padding: '12px 24px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
              {loading ? 'Loading...' : 'Load Dataset'}
            </button>
          </div>
        </div></div>
      )}

      {step === 1 && selectedDataset && (
        <div>
          <div style={gs}><div style={ghs}>📋 Dataset Summary</div><div style={gcs}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <Chip label={`${selectedDataset.rows?.toLocaleString()} rows`} sx={{ background: '#ecf0f1' }} />
              <Chip label={`${selectedDataset.columns?.length || 0} columns`} sx={{ background: '#ecf0f1' }} />
            </div>
          </div></div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={handleAnalyze} disabled={analyzing} style={{ padding: '10px 24px', background: analyzing ? '#95a5a6' : '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: analyzing ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
              {analyzing ? '🔍 Analyzing...' : '🔍 Analyze Missing Rate'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && results && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
            {[
              { label: 'Total Rows', value: results.total_rows?.toLocaleString(), color: '#3498db' },
              { label: 'Total Columns', value: results.total_columns, color: '#2ecc71' },
              { label: 'Cols with Missing', value: results.columns_with_missing, color: '#f39c12' },
              { label: 'Missing Cells', value: results.total_missing_cells?.toLocaleString(), color: '#e74c3c' },
            ].map((card, i) => (
              <Paper key={i} sx={{ p: 2, textAlign: 'center', borderLeft: `4px solid ${card.color}`, borderRadius: '8px' }}>
                <Typography variant="h5" fontWeight={600} sx={{ color: card.color }}>{card.value}</Typography>
                <Typography variant="caption" color="textSecondary">{card.label}</Typography>
              </Paper>
            ))}
          </div>

          <div style={gs}>
            <div style={ghs}>📋 Missing Values by Column</div>
            <div style={{ ...gcs, padding: 0 }}>
              {/* Header */}
              <div style={{ display: 'flex', background: '#2c3e50', color: 'white', fontWeight: 600, fontSize: '12px', padding: '10px 14px', borderBottom: '2px solid #1abc9c', position: 'sticky', top: 0 }}>
                <div style={{ width: '30%', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Column</div>
                <div style={{ width: '12%', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Missing</div>
                <div style={{ width: '35%', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Missing %</div>
                <div style={{ width: '10%', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)' }}>%</div>
                <div style={{ width: '13%', textAlign: 'center' }}>Non-Missing</div>
              </div>
              {/* Data Rows */}
              <div style={{ maxHeight: 500, overflow: 'auto' }}>
                {results.data?.map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '12px', borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                    <div style={{ width: '30%', padding: '10px 14px', fontFamily: 'monospace', borderRight: '1px solid #e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.Column}</div>
                    <div style={{ width: '12%', padding: '10px 14px', textAlign: 'center', borderRight: '1px solid #e0e0e0' }}>{row['Missing Count'].toLocaleString()}</div>
                    <div style={{ width: '35%', padding: '10px 14px', borderRight: '1px solid #e0e0e0' }}>
                      <div style={{ height: 12, background: '#eee', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(row['Missing %'], 100)}%`, background: row['Missing %'] > 50 ? '#e74c3c' : row['Missing %'] > 20 ? '#f39c12' : '#3498db', borderRadius: 6, minWidth: row['Missing %'] > 0 ? 4 : 0 }} />
                      </div>
                    </div>
                    <div style={{ width: '10%', padding: '10px 14px', textAlign: 'center', fontWeight: 600, borderRight: '1px solid #e0e0e0', color: row['Missing %'] > 50 ? '#e74c3c' : row['Missing %'] > 20 ? '#f39c12' : '#27ae60' }}>{row['Missing %']}%</div>
                    <div style={{ width: '13%', padding: '10px 14px', textAlign: 'center' }}>{row['Non-Missing Count'].toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={() => { setStep(0); setSelectedDataset(null); setResults(null); }} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>New Analysis</button>
            <button onClick={handleExport} style={{ padding: '8px 20px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissingRate;
